'use client';
import React, { useState } from 'react';
import { X, Layers, ChevronRight, Zap, BookOpen, BarChart2, BookMarked } from 'lucide-react';
import { PRESETS, type ArchitecturePreset } from '@/lib/archsim-data';



interface PresetsPanelProps {
  onLoad: (preset: ArchitecturePreset) => void;
  onClose: () => void;
}

const DIFFICULTY_COLORS = {
  Beginner:     'bg-positive/10 text-positive border-positive/20',
  Intermediate: 'bg-info/10 text-info border-info/20',
  Advanced:     'bg-warning/10 text-warning border-warning/20',
  Expert:       'bg-danger/10 text-danger border-danger/20',
};

const GROUPS = [
  {
    key: 'use-case',
    label: 'By Use Case',
    description: 'Real-world system architectures modelled after production apps',
    icon: Zap,
    iconColor: 'text-primary',
    badgeColor: 'bg-primary/10 text-primary border-primary/20',
  },
  {
    key: 'concept',
    label: 'By Concept / Educational',
    description: 'Illustrate specific distributed systems patterns and failure modes',
    icon: BookOpen,
    iconColor: 'text-info',
    badgeColor: 'bg-info/10 text-info border-info/20',
  },
  {
    key: 'complexity',
    label: 'By Complexity',
    description: 'Progressively complex setups from starter to multi-region',
    icon: BarChart2,
    iconColor: 'text-warning',
    badgeColor: 'bg-warning/10 text-warning border-warning/20',
  },
  {
    key: 'alex-xu-v1',
    label: 'Alex Xu Vol. 1',
    description: 'All 13 fundamental system designs from System Design Interview Volume 1 — mathematically grounded simulation',
    icon: BookMarked,
    iconColor: 'text-purple-400',
    badgeColor: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  },
  {
    key: 'alex-xu-v2',
    label: 'Alex Xu Vol. 2',
    description: 'All 13 advanced system designs from System Design Interview Volume 2 — production-grade architectures',
    icon: BookMarked,
    iconColor: 'text-pink-400',
    badgeColor: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  },
] as const;

type GroupKey = 'use-case' | 'concept' | 'complexity' | 'alex-xu-v1' | 'alex-xu-v2' | 'other';

function PresetCard({ preset, onLoad }: { preset: ArchitecturePreset; onLoad: (p: ArchitecturePreset) => void }) {
  return (
    <button
      key={`preset-${preset.id}`}
      onClick={() => onLoad(preset)}
      className="text-left p-3 sm:p-4 rounded-lg border border-border bg-secondary hover:border-primary/40 hover:bg-secondary/80 transition-all group"
    >
      <div className="flex items-start justify-between mb-1.5 sm:mb-2">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-primary shrink-0" />
          <span className="text-xs sm:text-sm font-semibold text-foreground leading-tight">{preset.name}</span>
        </div>
        <ChevronRight size={14} className="text-muted-foreground group-hover:text-primary transition-all shrink-0 mt-0.5" />
      </div>
      <p className="text-[11px] sm:text-xs text-muted-foreground mb-2 sm:mb-3 leading-relaxed line-clamp-2">{preset.description}</p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`skill-badge border ${DIFFICULTY_COLORS[preset.difficulty]}`}>
          {preset.difficulty}
        </span>
        <span className="skill-badge bg-secondary border border-border text-muted-foreground">
          {preset.category}
        </span>
        <span className="text-xs text-muted-foreground ml-auto font-mono-nums">
          {preset.nodes.length}n · {preset.edges.length}e
        </span>
      </div>
    </button>
  );
}

export default function PresetsPanel({ onLoad, onClose }: PresetsPanelProps) {
  const [activeGroup, setActiveGroup] = useState<GroupKey>('use-case');

  const grouped: Record<GroupKey, ArchitecturePreset[]> = {
    'use-case':    PRESETS.filter(p => p.group === 'use-case'),
    'concept':     PRESETS.filter(p => p.group === 'concept'),
    'complexity':  PRESETS.filter(p => p.group === 'complexity'),
    'alex-xu-v1':  PRESETS.filter(p => p.group === 'alex-xu-v1'),
    'alex-xu-v2':  PRESETS.filter(p => p.group === 'alex-xu-v2'),
    'other':       PRESETS.filter(p => !p.group),
  };

  const activePresets = grouped[activeGroup] ?? [];
  const activeGroupMeta = GROUPS.find(g => g.key === activeGroup);

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-xl w-full max-w-3xl overflow-hidden animate-slide-down sm:animate-scale-in flex flex-col max-h-[82dvh] sm:max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm sm:text-base font-semibold text-foreground">Architecture Presets</h2>
            <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">Load a pre-built architecture to explore common patterns</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all" aria-label="Close presets">
            <X size={16} />
          </button>
        </div>

        {/* Group tabs */}
        <div className="flex items-center gap-1 px-3 sm:px-6 pt-2.5 sm:pt-4 pb-0 shrink-0 border-b border-border overflow-x-auto scrollbar-thin">
          {GROUPS.map(group => {
            const GroupIcon = group.icon;
            const isActive = activeGroup === group.key;
            const count = grouped[group.key as GroupKey].length;
            const isAlexXu = group.key === 'alex-xu-v1' || group.key === 'alex-xu-v2';
            return (
              <button
                key={group.key}
                onClick={() => setActiveGroup(group.key as GroupKey)}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-[11px] sm:text-xs font-medium rounded-t-md border-b-2 transition-all whitespace-nowrap ${
                  isActive
                    ? isAlexXu
                      ? group.key === 'alex-xu-v1' ?'border-purple-400 text-purple-400 bg-purple-500/5' :'border-pink-400 text-pink-400 bg-pink-500/5' :'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                <GroupIcon size={13} />
                {group.label}
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                  isActive
                    ? isAlexXu
                      ? group.key === 'alex-xu-v1' ?'bg-purple-500/15 text-purple-400' :'bg-pink-500/15 text-pink-400' :'bg-primary/15 text-primary' :'bg-secondary text-muted-foreground'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
          {grouped.other.length > 0 && (
            <button
              onClick={() => setActiveGroup('other')}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-[11px] sm:text-xs font-medium rounded-t-md border-b-2 transition-all whitespace-nowrap ${
                activeGroup === 'other' ?'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
            >
              <Layers size={13} />
              Other
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                activeGroup === 'other' ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground'
              }`}>
                {grouped.other.length}
              </span>
            </button>
          )}
        </div>

        {/* Group description */}
        {activeGroupMeta && (
          <div className="px-4 sm:px-6 py-2 sm:py-3 bg-secondary/40 border-b border-border shrink-0">
            <p className="text-[11px] sm:text-xs text-muted-foreground line-clamp-2">{activeGroupMeta.description}</p>
          </div>
        )}

        {/* Presets grid */}
        <div className="p-3 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-4 overflow-y-auto scrollbar-thin flex-1">
          {activePresets.map(preset => (
            <PresetCard key={preset.id} preset={preset} onLoad={onLoad} />
          ))}
          {activePresets.length === 0 && (
            <div className="col-span-2 text-center py-12 text-muted-foreground text-sm">
              No presets in this group yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}